import {
  ApiRef,
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import { Draft, DraftListResponse, CategoryConfig, UserInfo } from '../types';

export interface CommsManagerApi {
  getMe(): Promise<UserInfo>;
  getCategories(): Promise<CategoryConfig[]>;
  listDrafts(options?: {
    status?: string;
    category?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<DraftListResponse>;
  getDraft(id: string): Promise<Draft>;
  createDraft(input: {
    title: string;
    body: string;
    category: string;
    tools: string[];
    link?: string;
  }): Promise<Draft>;
  updateDraft(
    id: string,
    input: {
      title?: string;
      body?: string;
      tools?: string[];
      category?: string;
      link?: string | null;
    },
  ): Promise<Draft>;
  transformDraft(
    action: 'translate' | 'summarize' | 'refine',
    title: string,
    body: string,
  ): Promise<{ title: string; body: string }>;
  uploadImage(file: File): Promise<string>;
  fetchImageAsObjectUrl(src: string): Promise<string>;
  approveDraft(id: string): Promise<Draft>;
  rejectDraft(id: string, reason?: string): Promise<Draft>;
  publishDraft(id: string): Promise<Draft>;
  closeDraft(id: string): Promise<Draft>;
  reopenDraft(id: string): Promise<Draft>;
  markRead(id: string): Promise<void>;
}

export const commsManagerApiRef: ApiRef<CommsManagerApi> =
  createApiRef<CommsManagerApi>({
    id: 'plugin.comms-manager.service',
  });

export class CommsManagerClient implements CommsManagerApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('comms-manager');
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers as Record<string, string>),
      },
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    if (response.status === 204) {
      return undefined as unknown as T;
    }

    return (await response.json()) as T;
  }

  async getMe(): Promise<UserInfo> {
    return this.request<UserInfo>('/me');
  }

  async getCategories(): Promise<CategoryConfig[]> {
    return this.request<CategoryConfig[]>('/categories');
  }

  async listDrafts(options?: {
    status?: string;
    category?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<DraftListResponse> {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.category) params.set('category', options.category);
    if (options?.search) params.set('search', options.search);
    if (options?.limit !== undefined)
      params.set('limit', String(options.limit));
    if (options?.offset !== undefined)
      params.set('offset', String(options.offset));
    const query = params.toString();
    const path = query ? `/drafts?${query}` : '/drafts';
    return this.request<DraftListResponse>(path);
  }

  async getDraft(id: string): Promise<Draft> {
    return this.request<Draft>(`/drafts/${encodeURIComponent(id)}`);
  }

  async createDraft(input: {
    title: string;
    body: string;
    category: string;
    tools: string[];
    link?: string;
  }): Promise<Draft> {
    return this.request<Draft>('/drafts', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateDraft(
    id: string,
    input: {
      title?: string;
      body?: string;
      tools?: string[];
      category?: string;
      link?: string | null;
    },
  ): Promise<Draft> {
    return this.request<Draft>(`/drafts/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  }

  async transformDraft(
    action: 'translate' | 'summarize' | 'refine',
    title: string,
    body: string,
  ): Promise<{ title: string; body: string }> {
    return this.request<{ title: string; body: string }>('/transform', {
      method: 'POST',
      body: JSON.stringify({ action, title, body }),
    });
  }

  async uploadImage(file: File): Promise<string> {
    const baseUrl = await this.discoveryApi.getBaseUrl('comms-manager');
    const buffer = await file.arrayBuffer();
    const base64 = btoa(
      new Uint8Array(buffer).reduce((s, b) => s + String.fromCodePoint(b), ''),
    );
    const response = await this.fetchApi.fetch(`${baseUrl}/uploads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        filename: file.name,
        mimeType: file.type,
        data: base64,
      }),
    });
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    const json = (await response.json()) as { id: string; url: string };
    return `${baseUrl}/uploads/${json.id}`;
  }

  async fetchImageAsObjectUrl(src: string): Promise<string> {
    // Resolve relative upload paths (e.g. /api/comms-manager/uploads/:id stored
    // by older articles) against the backend base URL so fetchApi sends the
    // request to the correct host with auth headers.
    let url = src;
    if (!src.startsWith('http://') && !src.startsWith('https://')) {
      const baseUrl = await this.discoveryApi.getBaseUrl('comms-manager');
      const path = src.startsWith('/api/comms-manager/')
        ? src.slice('/api/comms-manager'.length)
        : src;
      url = `${baseUrl}${path}`;
    }
    const response = await this.fetchApi.fetch(url);
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  async approveDraft(id: string): Promise<Draft> {
    return this.request<Draft>(`/drafts/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
    });
  }

  async rejectDraft(id: string, reason?: string): Promise<Draft> {
    return this.request<Draft>(`/drafts/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async publishDraft(id: string): Promise<Draft> {
    return this.request<Draft>(`/drafts/${encodeURIComponent(id)}/publish`, {
      method: 'POST',
    });
  }

  async closeDraft(id: string): Promise<Draft> {
    return this.request<Draft>(`/drafts/${encodeURIComponent(id)}/close`, {
      method: 'POST',
    });
  }

  async reopenDraft(id: string): Promise<Draft> {
    return this.request<Draft>(`/drafts/${encodeURIComponent(id)}/reopen`, {
      method: 'POST',
    });
  }

  async markRead(id: string): Promise<void> {
    await this.request<void>(`/drafts/${encodeURIComponent(id)}/mark-read`, {
      method: 'POST',
    });
  }
}
